import { useState } from 'react';
import Icon from '@components/common/Icon';
import { DATA_BASE_URL } from '@lib/constants';

const achievements = [
  {
    id: 1,
    title: 'AP Scholar with Distinction',
    description: 'Awarded for exceptional performance on multiple Advanced Placement examinations with distinction and honors.',
    image: `${DATA_BASE_URL}/data/awards/photos/ap_scholarship.webp`,
    certificate: `${DATA_BASE_URL}/data/awards/certificates/AP SCHOLAR WITH DISTINCTION.pdf`,
    type: 'photo',
    year: '2024',
    category: 'National Recognition',
    badge: 'AP Scholar',
    icon: 'academics'
  },
  {
    id: 2,
    title: 'National Honor Society 2025',
    description: 'Selected member of the National Honor Society, recognizing academic excellence, leadership, service, and character.',
    image: `${DATA_BASE_URL}/data/awards/photos/national_honor_society_2025.webp`,
    certificate: `${DATA_BASE_URL}/data/awards/certificates/MATH n SPANISH HONORS SOCIETY CERT AND MATHS DEPT AWARD CERT.pdf`,
    type: 'photo',
    year: '2025',
    category: 'National Honor',
    badge: 'NHS',
    icon: 'academics'
  },
  {
    id: 3,
    title: 'Spanish Honors Society 2025',
    description: 'Honored for outstanding achievement in Spanish language studies and cultural understanding.',
    image: `${DATA_BASE_URL}/data/awards/photos/spanish_honors_society_2025.webp`,
    certificate: `${DATA_BASE_URL}/data/awards/certificates/MATH n SPANISH HONORS SOCIETY CERT AND MATHS DEPT AWARD CERT.pdf`,
    type: 'photo',
    year: '2025',
    category: 'Language Excellence',
    badge: 'SHS',
    icon: 'academics'
  },
  {
    id: 4,
    title: 'IASAS Scholar Award 2025',
    description: 'International recognition for outstanding academic performance across all disciplines at the IASAS level.',
    image: `${DATA_BASE_URL}/data/awards/photos/iasas_scholar_award_2025.webp`,
    certificate: null,
    type: 'photo',
    year: '2025',
    category: 'International Recognition',
    badge: 'IASAS',
    icon: 'academics'
  },
  {
    id: 5,
    title: 'Mathematics Department Award 2023',
    description: 'Departmental recognition for exceptional achievement and dedication in mathematics coursework and problem-solving.',
    image: `${DATA_BASE_URL}/data/awards/photos/mathematics_department_award_2023.webp`,
    certificate: `${DATA_BASE_URL}/data/awards/certificates/ISMHonCert grade10, NM TITLE, MATH DEPT .pdf`,
    type: 'photo',
    year: '2023',
    category: 'Mathematics',
    badge: 'Math Dept',
    icon: 'research'
  },
  {
    id: 6,
    title: 'Mathematics Honors Society 2025',
    description: 'Membership in the Mathematics Honors Society for demonstrated excellence in mathematical studies.',
    image: `${DATA_BASE_URL}/data/awards/photos/mathematics_honors_society_2025.webp`,
    certificate: `${DATA_BASE_URL}/data/awards/certificates/MATH n SPANISH HONORS SOCIETY CERT AND MATHS DEPT AWARD CERT.pdf`,
    type: 'photo',
    year: '2025',
    category: 'Mathematics',
    badge: 'Math Honor',
    icon: 'research'
  }
];

const AchievementsShowcaseSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2
            className="text-4xl sm:text-5xl font-heading font-bold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            Academic Achievements
          </h2>
          <p
            className="text-lg sm:text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Recognition for excellence across multiple academic disciplines and national honor societies
          </p>
        </div>

        {/* Achievements Grid - Horizontal scroll on mobile, grid on desktop */}
        <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-6 lg:gap-8 overflow-x-auto md:overflow-x-visible">
          <div className="flex md:contents gap-6 pb-4 md:pb-0">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className="group relative rounded-xl overflow-hidden shadow-card transition-all duration-300 hover:shadow-card-hover flex-shrink-0 w-[320px] md:w-auto"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: hoveredCard === achievement.id ? 'var(--color-accent-6)' : 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid',
                transform: hoveredCard === achievement.id ? 'translateY(-4px)' : 'translateY(0)'
              }}
              onMouseEnter={() => setHoveredCard(achievement.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Image/Certificate Preview */}
              <div className="relative h-48 overflow-hidden" style={{ backgroundColor: 'var(--color-theme-2)' }}>
                <img
                  src={achievement.image}
                  alt={achievement.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />

                {/* Badge Overlay */}
                <div
                  className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold shadow-md"
                  style={{
                    backgroundColor: 'var(--color-accent-6)',
                    color: 'var(--color-button-primary-text)'
                  }}
                >
                  {achievement.badge}
                </div>

                {/* Year Badge */}
                <div
                  className="absolute bottom-3 left-3 px-3 py-1 rounded-full text-xs font-semibold"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    color: 'var(--color-accent-6)',
                    borderColor: 'var(--color-accent-5)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  {achievement.year}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-3">
                <div className="flex items-start gap-3">
                  <Icon
                    name={achievement.icon}
                    size="lg"
                    className="flex-shrink-0 mt-1"
                    style={{ color: 'var(--color-accent-6)' }}
                  />
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-xl font-bold mb-1 line-clamp-2"
                      style={{ color: 'var(--color-heading)' }}
                    >
                      {achievement.title}
                    </h3>
                    <p
                      className="text-sm font-medium mb-2"
                      style={{ color: 'var(--color-accent-6)' }}
                    >
                      {achievement.category}
                    </p>
                  </div>
                </div>

                <p
                  className="text-sm leading-relaxed line-clamp-3"
                  style={{ color: 'var(--color-subtle)' }}
                >
                  {achievement.description}
                </p>

                {/* View Link */}
                {achievement.certificate && (
                  <a
                    href={achievement.certificate}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold transition-colors duration-200 pt-2"
                    style={{ color: 'var(--color-accent-6)' }}
                  >
                    View PDF Certificate
                    <Icon name="arrow" size="sm" />
                  </a>
                )}
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AchievementsShowcaseSection;
